/**
 * AvizorIncomingModal — UI presentacional del modal bloqueante que se le
 * muestra al perito cuando entra una videoperitación inmediata.
 *
 * No tiene estado propio: recibe `avizor`, `secondsLeft`, `busy` y dos
 * callbacks `onAccept` / `onReject`. La lógica de polling, beep, navegación
 * y dismissal vive en `AvizorListener.jsx`.
 */
import { Phone, X, VideoCamera, MapPin, Warning, House } from "@phosphor-icons/react";

export default function AvizorIncomingModal({
  avizor,
  secondsLeft,
  busy = false,
  onAccept,
  onReject,
}) {
  if (!avizor) return null;
  const resumen = avizor.expediente_resumen || {};

  return (
    <div
      data-testid="avizor-modal"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in"
    >
      <div className="relative w-[min(560px,94vw)] bg-white rounded-2xl shadow-2xl overflow-hidden ring-2 ring-status-error-fg/60">
        {/* Cabecera roja con icono pulsante */}
        <div className="bg-status-error-fg text-white px-6 py-4 flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
            <div className="relative w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <VideoCamera className="w-5 h-5" weight="fill" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">
              Videoperitación inmediata
            </div>
            <div className="text-lg font-bold leading-tight">¡Avizor entrante!</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider opacity-80">Expira en</div>
            <div data-testid="avizor-countdown" className="text-2xl font-bold tabular-nums">
              {secondsLeft}s
            </div>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="px-6 py-4 space-y-3">
          <div className="text-[13px] text-foreground">
            <span className="font-semibold">{avizor.gestor_name || "Un gestor"}</span> te asigna
            una videoperitación con el{" "}
            <span className="font-semibold">
              {avizor.tipo === "asegurado" ? "asegurado" : "reparador"}
            </span>
            {avizor.contact_name ? (
              <>
                {" "}— <span className="font-semibold">{avizor.contact_name}</span>
              </>
            ) : null}
            .
          </div>

          <div className="bg-background border border-border rounded-lg p-3 space-y-1.5">
            <div className="flex items-center gap-2 text-[12px] text-muted">
              <House className="w-3.5 h-3.5" weight="duotone" />
              <span className="font-semibold text-foreground">
                {resumen.aseguradora || "—"}
              </span>
              {resumen.numero_siniestro ? (
                <span className="text-foreground">· {resumen.numero_siniestro}</span>
              ) : null}
            </div>
            {resumen.asegurado ? (
              <div className="text-[12px] text-muted">
                <span className="text-foreground">Asegurado:</span> {resumen.asegurado}
              </div>
            ) : null}
            {resumen.direccion ? (
              <div className="text-[12px] text-muted inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" weight="regular" /> {resumen.direccion}
              </div>
            ) : null}
            {avizor.contact_phone ? (
              <div className="text-[12px] text-muted inline-flex items-center gap-1">
                <Phone className="w-3.5 h-3.5" weight="regular" /> {avizor.contact_phone}
              </div>
            ) : null}
          </div>

          {avizor.message ? (
            <div className="bg-status-warning-bg border border-status-warning-border rounded-lg px-3 py-2 text-[12px] text-status-warning-fg flex items-start gap-2">
              <Warning className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" weight="duotone" />
              <span>{avizor.message}</span>
            </div>
          ) : null}
        </div>

        {/* Acciones */}
        <div className="px-6 pb-5 flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onReject}
            disabled={busy}
            data-testid="avizor-reject-btn"
            className="text-[13px] px-4 h-10 border border-border bg-white rounded-lg hover:bg-background inline-flex items-center gap-1.5 text-foreground transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" weight="bold" /> Rechazar
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={busy}
            data-testid="avizor-accept-btn"
            className="text-[13px] px-5 h-10 bg-status-error-fg hover:opacity-90 rounded-lg inline-flex items-center gap-1.5 text-white font-semibold transition-opacity disabled:opacity-50"
          >
            <VideoCamera className="w-4 h-4" weight="fill" />
            {busy ? "Conectando..." : "Aceptar y entrar"}
          </button>
        </div>
      </div>
    </div>
  );
}
